import React, { useState, useEffect } from "react";

// custom hooks
import useMediaQuery from "../common/custom-hooks/use-media-query";

// router
import { useLocation, useParams } from "react-router";

// compoenents
import SingleSelectEclinPro from "./singleselect-eclin-pro";
import { DelinkModalIcon } from "../../assets/icons/delink-modal-svg-icon";
import { BackButton } from "../common/back-button/back-button";
import SecondaryButton from "../common/buttons/SecondaryButton";

// api
import {
  getAllEclinProTrials,
  getEclinProConnectedTrial,
  linkEclinProTrial,
  delinkEclinProTrial,
} from "../../js/api/eclin-pro";

/* ========================================================== */

const EclinProStudy = ({ id, addToast }) => {
  const [apiLoader, setApiLoader] = useState(false);

  const [eclinProTrials, setEclinProTrials] = useState([]);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [isDelink, setIsDelink] = useState(null);
  const [eclinProTrialsDetailsID, setEclinProTrialsDetailsID] = useState(null);
  const [enableSave, setEnableSave] = useState(false);
  const params = useParams();

  /* ========= Fetch all trials ========= */
  useEffect(() => {
    setLoading(true);

    getAllEclinProTrials()
      .then((res) => {
        if (Array.isArray(res?.data?.data)) {
          setEclinProTrials(res.data.data);
        } else {
          setEclinProTrials([]);
        }
      })
      .catch(() => {
        setError("Failed to fetch EclinPro trials.");
      })
      .finally(() => setLoading(false));
  }, []);

  /* ========= Set selected dropdown from API ========= */
  useEffect(() => {
    getEclinProConnectedTrial(params.id)
      .then((res) => {
        const connected = res?.data?.data?.[0];
        if (connected?.eclinProTrialsDetailsID) {
          setEclinProTrialsDetailsID(connected.eclinProTrialsDetailsID);
        } else {
          setEclinProTrialsDetailsID(null);
          setSelectedTrial(null);
        }
      })
      .catch((err) => console.error(err));
  }, [params.id]);

  /* ========= Auto-select matched trial when list + ID ready ========= */
  useEffect(() => {
    // Do nothing until trials list is ready
    if (!eclinProTrials.length) return;

    // Case 1: No mapped trial from API → selectedTrial must be null
    if (eclinProTrialsDetailsID === null) {
      // Set null only if current is not null (prevent flicker!)
      if (selectedTrial !== null) {
        setSelectedTrial(null);
      }
      return;
    }

    // Case 2: API returned an ID → find match
    const match = eclinProTrials.find(
      (trial) => trial.eclinProTrialsDetailsID === eclinProTrialsDetailsID
    );

    if (match) {
      const formattedMatch = {
        value: match.eclinProTrialsDetailsID,
        label: match.displayName,
        ...match,
      };

      // Prevent re-setting state if already same (prevent flicker!)
      if (selectedTrial?.value !== formattedMatch.value) {
        setSelectedTrial(formattedMatch);
      }
    } else {
      // No matching trial — set null only if needed
      if (selectedTrial !== null) {
        setSelectedTrial(null);
      }
    }
  }, [eclinProTrials, eclinProTrialsDetailsID]);

  /* ========= Manual Selection ========= */
  const handleEclinProChange = (selected) => {
    setSelectedTrial(selected);
  };

  /* ========= Link Trial ========= */
  const link = () => {
    if (!selectedTrial) {
      addToast("Please select an eClinPro trial before linking.", "error");
      return;
    }

    setApiLoader(true);

    const payload = {
      trialID: params.id,
      eclinProTrialsDetailsID: selectedTrial.eclinProTrialsDetailsID,
    };

    linkEclinProTrial(payload)
      .then(() => {
        setApiLoader(false);
        addToast(
          `${selectedTrial.displayName} study has been successfully linked.`,
          "success"
        );
        setEnableSave(false);
        setEclinProTrialsDetailsID(selectedTrial.eclinProTrialsDetailsID);
      })
      .catch(() => {
        addToast(`Failed to link ${selectedTrial.displayName}.`, "error");
        setApiLoader(false);
      });
  };

  useEffect(() => {
    if (eclinProTrialsDetailsID === undefined) return; // initial load

    const original = eclinProTrialsDetailsID || null;
    const current = selectedTrial?.eclinProTrialsDetailsID || null;

    const changed = original !== current;

    if (changed && !enableSave) setEnableSave(true);
    if (!changed && enableSave) setEnableSave(false);
  }, [selectedTrial, eclinProTrialsDetailsID]);

  const delink = () => {
    const payload = {
      trialID: params.id,
      eclinProTrialsDetailsID: eclinProTrialsDetailsID,
    };

    setApiLoader(true);

    delinkEclinProTrial(payload)
      .then(() => {
        addToast(
          `${selectedTrial.displayName} study successfully disconnected.`,
          "success"
        );
        setSelectedTrial(null);
        setEclinProTrialsDetailsID(null);
        setEnableSave(false);
        setIsDelink(false);
      })
      .catch(() => {
        addToast("Failed to disconnect.", "error");
      })
      .finally(() => setApiLoader(false));
  };

  useEffect(() => {
    console.log("enableSave", enableSave);
    console.log("isDelink", isDelink);
  }, [enableSave, isDelink]);

  return (
    <>
      <div className="h-[calc(100vh-244px)] overflow-y-auto p-5 pt-0">
        {/* ========== EclinPro Dropdown ========== */}
        <SingleSelectEclinPro
          options={eclinProTrials}
          selectedOption={selectedTrial}
          setSelectedOption={setSelectedTrial}
          onChange={handleEclinProChange}
          setEnableSave={setEnableSave}
          setIsDelink={setIsDelink}
          eclinProTrialsDetailsID={eclinProTrialsDetailsID}
        />
        <div className="mt-[10px]">
          {selectedTrial ? (
            <>
              Selected:{" "}
              <span className="font-semibold">{selectedTrial.displayName}</span>
              . Click the <span className="font-semibold">✕</span> to clear
            </>
          ) : (
            ""
          )}
        </div>

        {error && <p className="mt-3 text-red-500">{error}</p>}

      </div>

      {/* ========== Save Button ========== */}
      {selectedTrial && enableSave ? (
        <div className="fixed shadow-[0_-4px_20px_0_rgba(0,0,0,0.25)] p-4 bottom-0 w-full bg-white pl-8 flex gap-5">
          <button
            type="submit"
            className="px-5 py-1 text-base rounded-md border primary_btn transition-all duration-500 min-w-[94px]"
            disabled={apiLoader}
            onClick={link}
          >
            {apiLoader ? (
              <div className="flex justify-center">
                <div className="mini-spinner"></div>
              </div>
            ) : (
              "Save"
            )}
          </button>

          <BackButton editTrial={true} />
        </div>
      ) : null}
      {isDelink ? (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50">
          <div className="bg-white w-[525px] md:w-[525px] rounded-lg shadow-lg p-[30px]">
            <div className="pt-0">
              <div className=" mb-[30px]">
                {/* <img src={imageSrc} className={`w-12 h-12 mr-4`} /> */}
                <div className="flex">
                  <div>
                    <DelinkModalIcon className={"mr-5"} />
                  </div>
                  <h3 className="font-mont text-[16px] font-medium text-[#1B2A3D]">
                    <span>Are you sure you want to disconnect</span>&nbsp;
                    <span className="font-mont font-semibold text-[#1B2A3D]">
                      {isDelink.displayName}
                    </span>{" "}
                    ?
                  </h3>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  className="px-5 py-1 rounded-md border primary_btn transition-all duration-500 red_btn h-[42px] text-[14px] font-mont font-[500] mr-[20px]"
                  onClick={delink}
                >
                  Disconnect
                </button>
                <SecondaryButton
                  label="Cancel"
                  onClick={() => setIsDelink(false)}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};

export default EclinProStudy;
